import React, { useState, useRef } from 'react';
import { supabase } from './supabaseClient';

const COLORS = {
  gold: '#f0c040',
  cyan: '#00d4ff',
  green: '#00e87a',
  red: '#ff4444',
  bg: '#04060f',
  cardBg: '#0a1020',
  muted: '#4a5a7a',
};

const SCAN_BUCKET = 'scan-test-images';

function compressImage(file, maxDim = 2200, quality = 0.95, enhance = false) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => {
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        if (enhance) {
          // Safe enhancement: mild exposure + contrast boost via linear pixel math only.
          // No composite ops, no blur tricks — those produced hallucinations during testing.
          const imageData = ctx.getImageData(0, 0, width, height);
          const d = imageData.data;
          const exposure = 1.10;
          const contrast = 1.12;
          const midpoint = 128;
          for (let i = 0; i < d.length; i += 4) {
            let r = d[i] * exposure;
            let g = d[i + 1] * exposure;
            let b = d[i + 2] * exposure;
            r = (r - midpoint) * contrast + midpoint;
            g = (g - midpoint) * contrast + midpoint;
            b = (b - midpoint) * contrast + midpoint;
            d[i] = Math.max(0, Math.min(255, r));
            d[i + 1] = Math.max(0, Math.min(255, g));
            d[i + 2] = Math.max(0, Math.min(255, b));
          }
          ctx.putImageData(imageData, 0, 0);
        }

        canvas.toBlob(
          (blob) => blob ? resolve(blob) : reject(new Error('Compression failed')),
          'image/jpeg',
          quality,
        );
      };
      img.onerror = () => reject(new Error('Image load failed'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('File read failed'));
    reader.readAsDataURL(file);
  });
}

function PrettyResult({ data, elapsedMs }) {
  if (!data) return null;
  const d = (data && data.data) || data || {};
  const conf = d.confidence || {};

  const rows = [
    ['Set', d.set_name],
    ['Set ID', d.set_id],
    ['Sub-set type', d.sub_set_type],
    ['Card name', d.card_name],
    ['Card number', d.card_number],
    ['Serial #', d.serial_number],
    ['Print run', d.serial_print_run],
    ['Parallel', d.parallel_name],
    ['Parallel — evidence', d.parallel_front_evidence],
    ['Serial observation', d.serial_raw_observation],
    ['Graded?', d.is_graded],
    ['Grade company', d.grading_company],
    ['Grade', d.grading_grade],
    ['Cert #', d.grading_cert_num],
    ['Notes', d.notes],
    ['Confidence — identity', conf.card_identity],
    ['Confidence — serial', conf.serial_number],
    ['Confidence — grading', conf.grading],
  ];

  return (
    <div style={{ background: COLORS.cardBg, padding: 16, borderRadius: 8, marginTop: 16 }}>
      <div style={{ fontFamily: 'Orbitron, sans-serif', color: COLORS.gold, marginBottom: 12, fontSize: 14 }}>
        CLAUDE RESPONSE
      </div>
      <div style={{ fontFamily: 'Share Tech Mono, monospace', color: COLORS.muted, fontSize: 12, marginBottom: 12 }}>
        Elapsed: {elapsedMs}ms
      </div>
      <table style={{ width: '100%', fontFamily: 'Rajdhani, sans-serif', color: '#fff', fontSize: 14 }}>
        <tbody>
          {rows.map(([label, val], i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${COLORS.muted}22` }}>
              <td style={{ padding: '6px 8px', color: COLORS.cyan, width: '40%' }}>{label}</td>
              <td style={{ padding: '6px 8px', color: '#fff', wordBreak: 'break-word' }}>
                {val === null || val === undefined || val === '' ? '—' : String(val)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ScanId() {
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [result, setResult] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);

  const pickerRef = useRef(null);
  const [picking, setPicking] = useState(null);

  const openPicker = (side) => {
    setPicking(side);
    if (pickerRef.current) {
      pickerRef.current.value = '';
      pickerRef.current.click();
    }
  };

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    if (picking === 'front') {
      setFrontFile(file);
      setFrontPreview(url);
    } else {
      setBackFile(file);
      setBackPreview(url);
    }
    setPicking(null);
  };

  const clearAll = () => {
    setFrontFile(null);
    setBackFile(null);
    setFrontPreview(null);
    setBackPreview(null);
    setResult(null);
    setStatus('');
    setElapsedMs(0);
  };

  const scan = async () => {
    if (!frontFile || !backFile) {
      setStatus('Please select both front and back photos.');
      return;
    }
    setBusy(true);
    setResult(null);
    setStatus('Compressing photos...');

    const startedAt = performance.now();

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData?.session;
      if (!session) {
        setStatus('Not signed in. Please log in first.');
        setBusy(false);
        return;
      }
      const userId = session.user.id;

      const [frontBlob, backBlob] = await Promise.all([
        compressImage(frontFile, 2200, 0.95, false),
        compressImage(backFile, 2200, 0.95, true),
      ]);

      setStatus('Uploading photos...');
      const ts = Date.now();
      const frontPath = `${userId}/scanid-${ts}-front.jpg`;
      const backPath = `${userId}/scanid-${ts}-back.jpg`;

      const [frontUp, backUp] = await Promise.all([
        supabase.storage.from(SCAN_BUCKET).upload(frontPath, frontBlob, {
          contentType: 'image/jpeg',
        }),
        supabase.storage.from(SCAN_BUCKET).upload(backPath, backBlob, {
          contentType: 'image/jpeg',
        }),
      ]);
      if (frontUp.error) throw new Error(`Front upload: ${frontUp.error.message}`);
      if (backUp.error) throw new Error(`Back upload: ${backUp.error.message}`);

      const frontUrl = supabase.storage.from(SCAN_BUCKET).getPublicUrl(frontPath).data.publicUrl;
      const backUrl = supabase.storage.from(SCAN_BUCKET).getPublicUrl(backPath).data.publicUrl;

      setStatus('Claude is analyzing the card...');

      const { data, error } = await supabase.functions.invoke('extract-card-info', {
        body: {
          mode: 'identify',
          frontImageUrl: frontUrl,
          backImageUrl: backUrl,
        },
      });

      const elapsed = Math.round(performance.now() - startedAt);
      setElapsedMs(elapsed);

      if (error) {
        setStatus(`Edge function error: ${error.message}`);
        setResult(null);
      } else {
        setResult(data);
        setStatus(`Scan complete in ${(elapsed / 1000).toFixed(1)}s`);
      }
    } catch (err) {
      setStatus(`Error: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: COLORS.bg,
      padding: '16px',
      fontFamily: 'Rajdhani, sans-serif',
      color: '#fff',
    }}>
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <div style={{
          fontFamily: 'Orbitron, sans-serif',
          color: COLORS.gold,
          fontSize: 20,
          letterSpacing: 1,
          marginBottom: 4,
        }}>
          AI CARD SCANNER
        </div>
        <div style={{
          fontFamily: 'Share Tech Mono, monospace',
          color: COLORS.green,
          fontSize: 11,
          letterSpacing: 2,
          marginBottom: 24,
        }}>
          STAGING · IDENTIFICATION MODE
        </div>

        <p style={{ color: COLORS.muted, fontSize: 14, marginBottom: 20, lineHeight: 1.5 }}>
          Take photos of the front and back of any 2024 Topps Chrome Star Wars Galaxy card.
          Claude will identify the card on its own — no expected-card info needed.
        </p>

        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <button
              onClick={() => openPicker('front')}
              disabled={busy}
              style={{
                width: '100%',
                padding: '12px',
                background: frontPreview ? COLORS.cardBg : 'transparent',
                color: COLORS.cyan,
                border: `1px solid ${COLORS.cyan}`,
                borderRadius: 4,
                fontFamily: 'Orbitron, sans-serif',
                fontSize: 12,
                letterSpacing: 1,
                cursor: busy ? 'not-allowed' : 'pointer',
              }}
            >
              {frontPreview ? 'RETAKE FRONT' : 'ADD FRONT PHOTO'}
            </button>
            {frontPreview && (
              <img src={frontPreview} alt="front" style={{ width: '100%', marginTop: 8, borderRadius: 4 }} />
            )}
          </div>
          <div style={{ flex: 1 }}>
            <button
              onClick={() => openPicker('back')}
              disabled={busy}
              style={{
                width: '100%',
                padding: '12px',
                background: backPreview ? COLORS.cardBg : 'transparent',
                color: COLORS.cyan,
                border: `1px solid ${COLORS.cyan}`,
                borderRadius: 4,
                fontFamily: 'Orbitron, sans-serif',
                fontSize: 12,
                letterSpacing: 1,
                cursor: busy ? 'not-allowed' : 'pointer',
              }}
            >
              {backPreview ? 'RETAKE BACK' : 'ADD BACK PHOTO'}
            </button>
            {backPreview && (
              <img src={backPreview} alt="back" style={{ width: '100%', marginTop: 8, borderRadius: 4 }} />
            )}
          </div>
        </div>

        <input
          ref={pickerRef}
          type="file"
          accept="image/*"
          onChange={onPick}
          style={{ display: 'none' }}
        />

        <button
          onClick={scan}
          disabled={busy || !frontFile || !backFile}
          style={{
            width: '100%',
            padding: '14px',
            background: (busy || !frontFile || !backFile) ? COLORS.muted : COLORS.gold,
            color: (busy || !frontFile || !backFile) ? '#000' : COLORS.bg,
            border: 'none',
            borderRadius: 4,
            fontFamily: 'Orbitron, sans-serif',
            fontSize: 14,
            letterSpacing: 2,
            fontWeight: 700,
            cursor: (busy || !frontFile || !backFile) ? 'not-allowed' : 'pointer',
            marginBottom: 12,
          }}
        >
          {busy ? 'SCANNING...' : 'IDENTIFY CARD'}
        </button>

        <button
          onClick={clearAll}
          disabled={busy}
          style={{
            width: '100%',
            padding: '10px',
            background: 'transparent',
            color: COLORS.muted,
            border: `1px solid ${COLORS.muted}`,
            borderRadius: 4,
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: 12,
            letterSpacing: 1,
            cursor: busy ? 'not-allowed' : 'pointer',
          }}
        >
          CLEAR
        </button>

        {status && (
          <div style={{
            marginTop: 16,
            padding: 12,
            background: COLORS.cardBg,
            borderRadius: 4,
            fontFamily: 'Share Tech Mono, monospace',
            color: COLORS.green,
            fontSize: 12,
          }}>
            {status}
          </div>
        )}

        <PrettyResult data={result} elapsedMs={elapsedMs} />

        {result && (
          <details style={{ marginTop: 16 }}>
            <summary style={{ color: COLORS.muted, fontSize: 12, cursor: 'pointer' }}>Raw JSON</summary>
            <pre style={{
              background: COLORS.cardBg,
              padding: 12,
              borderRadius: 4,
              color: COLORS.green,
              fontFamily: 'Share Tech Mono, monospace',
              fontSize: 11,
              overflow: 'auto',
              marginTop: 8,
            }}>
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
